import { GitHubIcon } from '@backstage/core-components';
import { featureFlagsApiRef, useApi } from '@backstage/frontend-plugin-api';
import { Container } from '@backstage/ui';
import {
  CardType,
  DashboardCard,
  Page,
} from '@internal/backstage-plugin-hm-react';
import { Grid } from '@material-ui/core';
import {
  RiBarChartGroupedLine,
  RiDashboardFill,
  RiLineChartFill,
  RiDashboard3Line,
  RiMoneyDollarCircleFill,
  RiPulseLine,
  RiGradienterLine,
} from '@remixicon/react';

export const DashboardsLandingPageIcon = <RiDashboardFill />;

export const DashboardsLandingPage = () => {
  const lensEnabled = useApi(featureFlagsApiRef).isActive('lens');

  return (
    <Page title="Dashboards">
      <Container>
        <Grid container spacing={3} direction="row">
          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/dora-insights.png"
              title="DORA Insights"
              type={CardType.service}
              description={`Track deployment frequency and lead time across GitHub
                repositories with interactive timelines and filters.`}
              actionRoute="/dashboards/dora"
              Icon={RiLineChartFill}
              fullHeight
              owner="dy-tm-delivery-sre"
            />
          </Grid>

          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/github-dashboard.png"
              title="GitHub Dashboard"
              type={CardType.dashboard}
              description={`The GitHub Dashboard provides insights into the open pull
                requests, issues and security vulnerabilities of your team's
                GitHub repositories.`}
              actionRoute="/dashboards/github"
              Icon={GitHubIcon}
              fullHeight
              owner="dy-tm-developer-tools"
            />
          </Grid>

          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/tool-onboarding-dashboard.png"
              title="Tool Onboarding Dashboard"
              type={CardType.dashboard}
              description={`Visualize the tools assigned to you, compare catalog pricing,
                and submit offboarding or onboarding requests.`}
              actionRoute="/dashboards/tool-onboarding-dashboard"
              Icon={RiMoneyDollarCircleFill}
              fullHeight
              owner="dy-tm-developer-tools"
            />
          </Grid>

          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/github-statistics.png"
              title="GitHub Statistics"
              type={CardType.dashboard}
              actionRoute="/github-statistics"
              Icon={GitHubIcon}
              fullHeight
              owner="dy-tm-developer-tools"
            />
          </Grid>

          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/operational-maturity.png"
              title="Operational Maturity"
              type={CardType.service}
              description={`Run a structured maturity assessment across Baseline,
                Intermediate, and Expert capabilities. Save drafts and submit
                for review.`}
              actionRoute="/dashboards/operational-maturity"
              Icon={RiBarChartGroupedLine}
              fullHeight
              owner="dy-tm-delivery-sre"
            />
          </Grid>

          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/pulse.png"
              title="Pulse"
              type={CardType.plugin}
              description="Pulse is our next-generation engineering health and standards platform, built to replace Soundcheck with a solution tailored to our needs."
              actionRoute="/pulse"
              Icon={RiPulseLine}
              fullHeight
              owner="dy-tm-developer-tools"
            />
          </Grid>

          {lensEnabled && (
            <Grid item sm={12} md={6} lg={3} xl={2}>
              <DashboardCard
                media="/dashboards/lens.png"
                title="Lens"
                type={CardType.plugin}
                description="Lens is our observability platform, providing insights into metrics, logs, and traces to help understand and improve system performance."
                actionRoute="/lens"
                Icon={RiGradienterLine}
                fullHeight
                owner="dy-tm-developer-tools"
              />
            </Grid>
          )}

          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/threads.png"
              title="Threads Dashboard"
              type={CardType.dashboard}
              description={`Explore the usage of Threads in our landscape.\n\nThis dashboard
                provides insights into the number of applications using Threads,
                their status, and key metrics to follow the value and adoption
                of our microservice platform.`}
              actionRoute="https://app.powerbi.com/groups/f6651e6b-e40b-415c-be6a-b4988863fa6b/reports/8fb31a7c-0733-44a2-862e-c07fad67b439/e9c07a4e29a10ead875e?experience=power-bi"
              Icon={RiDashboard3Line}
              fullHeight
              owner="threads-maintainers"
            />
          </Grid>
          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/test-metrics.png"
              title="Test Metrics Dashboard"
              type={CardType.dashboard}
              description={`The dashboard shows automation coverage, manual vs automated test counts, and project-level progress to quickly assess automation maturity and other test metrics for testing performed in lower test environments.\n\nThis dashboard is a key tool for tracking progress towards our goal of 80% automation coverage across all projects.`}
              actionRoute="https://app.powerbi.com/groups/c408001a-d387-40d5-a1f7-7905b06dc7d6/reports/280b9691-39c7-4e2d-a0e1-703b1e7043f4/11d1693f38d238cda7db?experience=power-bi"
              Icon={RiDashboard3Line}
              fullHeight
              owner="automation-team"
            />
          </Grid>
          <Grid item sm={12} md={6} lg={3} xl={2}>
            <DashboardCard
              media="/dashboards/qe-dashboard.jpg"
              title="QE Automation Health Dashboard"
              type={CardType.dashboard}
              description="The QE Automation Health Dashboard provides a centralized view of automation testing health across repositories, workflows, failures, and test coverage. It enables teams and stakeholders to monitor quality trends, identify high-risk areas through dynamic alerts, and make data-driven decisions using automatically refreshed execution data."
              actionRoute="https://app.powerbi.com/groups/b41da406-f93a-4f56-bff9-dcca1ca82f8f/reports/2da30e4d-5cd5-41d1-aff7-e0f26d1b2b35/8335ff4030cc9331ced1?experience=power-bi"
              Icon={RiDashboard3Line}
              fullHeight
              owner="dy-tm-delivery-excellence"
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};
